import { Navigate } from "react-router-dom";
import { useSession } from "../hooks/use-session.js";
import { useOrgs } from "../hooks/use-orgs.js";
import { useWebsites } from "../hooks/use-websites.js";
import { useWizardState } from "../hooks/use-wizard.js";
import { PageSkeleton } from "../components/skeleton.js";

/**
 * Decides where an authenticated user should land:
 *  - admin user          → /admin
 *  - no org              → /setup/org
 *  - no website          → /setup/website
 *  - wizard completed    → /websites/:websiteId
 *  - wizard in progress  → /wizard/:websiteId
 */
export function RootRedirect() {
  const { isLoading: sessionLoading, user } = useSession();

  // Admins bypass wizard entirely and go straight to admin dashboard
  if (user?.isAdmin) {
    return <Navigate to="/admin" replace />;
  }

  const { data: orgsData, isLoading: orgsLoading } = useOrgs();
  const org = orgsData?.orgs[0] ?? null;
  const { data: websitesData, isLoading: websitesLoading } = useWebsites(
    org?.id,
  );
  const website = websitesData?.websites[0] ?? null;
  const { data: wizardState, isLoading: wizardLoading } = useWizardState(
    website?.id,
  );

  if (
    sessionLoading ||
    orgsLoading ||
    (org != null && websitesLoading) ||
    (website != null && wizardLoading)
  ) {
    return <PageSkeleton />;
  }

  if (!org) {
    return <Navigate to="/setup/org" replace />;
  }

  if (!website) {
    return <Navigate to="/setup/website" replace />;
  }

  if (wizardState?.completed) {
    return <Navigate to={`/websites/${website.id}`} replace />;
  }

  return <Navigate to={`/wizard/${website.id}`} replace />;
}
